import React, {
  createContext,
  useState,
  useEffect,
  useContext,
  useMemo,
  useCallback,
} from "react";
import axios from "axios";
import api from "../api";

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [token, setToken] = useState(() => localStorage.getItem("authToken"));
  const [refreshToken, setRefreshToken] = useState(() =>
    localStorage.getItem("refreshToken")
  );

  const [profile, setProfile] = useState(null);
  const [profileLoading, setProfileLoading] = useState(false);
  const [error, setError] = useState(null);

  // ✅ logout
  const logout = useCallback(() => {
    localStorage.removeItem("authToken");
    localStorage.removeItem("refreshToken");

    setToken(null);
    setRefreshToken(null);
    setProfile(null);
    setError(null);
    setProfileLoading(false);
  }, []);

  // ✅ refresh token
  const refreshAccessToken = useCallback(
    async (rToken) => {
      if (!rToken) throw new Error("No refresh token");

      const response = await axios.post(api.refreshToken, { token: rToken });

      const { accessToken, refreshToken: newRefreshToken } = response.data;

      localStorage.setItem("authToken", accessToken);
      setToken(accessToken);

      if (newRefreshToken) {
        localStorage.setItem("refreshToken", newRefreshToken);
        setRefreshToken(newRefreshToken);
      }

      return accessToken;
    },
    []
  );

  // ✅ fetch profile
  const fetchUserProfile = useCallback(
    async (authTokenParam) => {
      if (!authTokenParam) return;

      setProfileLoading(true);
      setError(null);

      try {
        const response = await axios.get(api.getProfile, {
          headers: { Authorization: `Bearer ${authTokenParam}` },
        });

        setProfile(response.data);
      } catch (err) {
        // try refresh if 401
        if (err.response?.status === 401 && refreshToken) {
          try {
            const newToken = await refreshAccessToken(refreshToken);

            const retryResponse = await axios.get(api.getProfile, {
              headers: { Authorization: `Bearer ${newToken}` },
            });

            setProfile(retryResponse.data);
          } catch (refreshErr) {
            setError("Session expired. Please login again.");
            logout();
          }
        } else {
          setError(err.response?.data?.message || "Failed to fetch profile");
        }
      } finally {
        setProfileLoading(false);
      }
    },
    [refreshToken, refreshAccessToken, logout]
  );

  // ✅ ONLY RUN when token changes (not on every render)
  useEffect(() => {
    if (token) {
      fetchUserProfile(token);
    } else {
      setProfile(null);
    }
  }, [token, fetchUserProfile]);

  // ✅ login
  const login = useCallback(async (newToken, newRefreshToken = null) => {
    localStorage.setItem("authToken", newToken);
    setToken(newToken);

    if (newRefreshToken) {
      localStorage.setItem("refreshToken", newRefreshToken);
      setRefreshToken(newRefreshToken);
    }
  }, []);

  const isLoggedIn = useMemo(() => !!token, [token]);

  const getRoles = useCallback(() => {
    return profile?.role || [];
  }, [profile]);

  const getBlockedRoles = useCallback(() => {
    return profile?.blocked || [];
  }, [profile]);

  const getUnblockedRoles = useCallback(() => {
    const roles = profile?.role || [];
    const blocked = profile?.blocked || [];
    return roles.filter((r) => !blocked.includes(r));
  }, [profile]);

  const setUserStorage = useCallback(
    (key, value) => {
      if (!token) return;
      localStorage.setItem(`${token}_${key}`, JSON.stringify(value));
    },
    [token]
  );

  const getUserStorage = useCallback(
    (key) => {
      if (!token) return null;
      const stored = localStorage.getItem(`${token}_${key}`);
      return stored ? JSON.parse(stored) : null;
    },
    [token]
  );

  return (
    <AuthContext.Provider
      value={{
        token,
        refreshToken,
        profile,
        profileLoading,
        error,
        login,
        logout,
        refreshAccessToken,
        fetchUserProfile,
        isLoggedIn,
        getRoles,
        getBlockedRoles,
        getUnblockedRoles,
        setUserStorage,
        getUserStorage,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
};
